import React from 'react';

import { Box, Stack, Typography, useTheme } from '@mui/material';

import { PERFORMANCE_STATUS, PERFORMANCE_STATUS_CONFIG } from './constants';
import { formatRangeLabel } from './utils';

import { useColorTokens } from '@/app/themes';
import { usePlatform } from '@/features/users';

interface TooltipPayloadItem {
  value?: number;
  dataKey?: PERFORMANCE_STATUS;
  fill?: string;
}

interface PerformanceMonitoringCustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: number;
}

export const PerformanceMonitoringCustomTooltip: React.FC<
  PerformanceMonitoringCustomTooltipProps
> = ({ active, payload = [], label }) => {
  const colorTokens = useColorTokens();
  const theme = useTheme();
  const platform = usePlatform();
  const isDV360 = platform === 'DV360';

  const hasNonZeroData = payload.some((entry) => (entry.value ?? 0) > 0);

  if (!active || !payload.length || !hasNonZeroData) {
    return null;
  }

  const nonNullLabel = label ?? 0;
  const labelColor =
    nonNullLabel < 0
      ? colorTokens.green[200]
      : nonNullLabel > 0
        ? colorTokens.red[200]
        : theme.palette.text.primary;

  return (
    <Box
      sx={[
        (theme) => ({
          backgroundColor: 'background.paper',
          p: 1.5,
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 1,
          boxShadow: theme.customShadows.lg,
        }),
        (theme) =>
          theme.applyStyles('dark', {
            border: 0,
          }),
      ]}
    >
      <Typography
        variant='subtitle2'
        sx={{
          fontWeight: 700,
          mb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 0.5,
        }}
      >
        Cost vs. Baseline:{' '}
        <span style={{ color: labelColor }}>
          {formatRangeLabel(nonNullLabel)}
        </span>
      </Typography>

      <Stack spacing={1}>
        {payload.map((entry, i) => {
          const value = entry.value ?? 0;
          const dataKey = entry.dataKey;
          if (value <= 0 || !dataKey) {
            return null;
          }

          const config = PERFORMANCE_STATUS_CONFIG[dataKey];
          if (!config.starvation || !config.spendForecast) {
            return null;
          }

          const starvColor =
            config.starvation.intent === 'success'
              ? colorTokens.green[200]
              : colorTokens.red[200];
          const spendColor =
            config.spendForecast.intent === 'success'
              ? colorTokens.green[200]
              : colorTokens.red[200];

          return (
            <Stack key={i} spacing={0.5}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Box
                  sx={{ width: 12, height: 12, backgroundColor: entry.fill }}
                />
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                  {value} {isDV360 ? 'Insertion Orders' : 'Campaigns'}
                </Typography>
              </Stack>

              <Box
                sx={{
                  pl: 3,
                  color: theme.palette.text.primary,
                  fontSize: 12,
                  textAlign: 'left',
                }}
              >
                <div>
                  Starvation:{' '}
                  <span style={{ color: starvColor, fontWeight: 700 }}>
                    {config.starvation.label}
                  </span>
                </div>
                <div>
                  Spend Forecast:{' '}
                  <span style={{ color: spendColor, fontWeight: 700 }}>
                    {config.spendForecast.label}
                  </span>
                </div>
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};
